import { createElement, lazy, Suspense } from 'react';
import type { ClientArea } from '../types';
import { registerCommands, registerGlobal, registerSlot, registerView, type SlotProps } from '@/lib/slots';
import { registerActions } from '@/lib/actions';
import { navigate } from '@/lib/router';
import { queryClient } from '@/lib/queryClient';
import { toast, toastError } from '@/lib/toast';
import { api } from '@/lib/api';
import { plural } from '@/lib/format';
import { rememberRecent } from '@/lib/recent';
import { confirmDialog } from '@/ui/Dialog';
import { DEFAULT_COUNT, MODE_LABELS, type FinderSearchSummary } from '@shared/finder';
import { draftStore, metaQuery, startSearch } from './api';
import { FinderLive } from './Live';

// AI Finder: searches the web for companies and people to add, fills in missing details on companies and people, and
// finds the key people at companies already in Clientbook.

const RecordFill = lazy(() => import('./RecordFill'));
const RecordPeople = lazy(() => import('./RecordPeople'));

/** The AI review on a company or person record. Its code loads with the first record opened; until then nothing shows. */
function RecordFillSlot(props: SlotProps['record.fill']) {
  return createElement(Suspense, { fallback: null }, createElement(RecordFill, props));
}

/** Finding a company's key people, under its people. Its code loads with the first company opened. */
function RecordPeopleSlot(props: SlotProps['record.people']) {
  return createElement(Suspense, { fallback: null }, createElement(RecordPeople, props));
}

/** Shows the Finder's search form for a mode, with the cursor in it. */
function openForm(mode: 'describe' | 'like' | 'filters' | 'fill'): void {
  navigate({ view: 'finder', record: null, admin: null, params: { mode, s: null } });
  draftStore.set((d) => ({ focus: d.focus + 1 }));
}

/** Opens Describe with the text filled in, then asks before spending money on the search. */
async function finderAsk(description: string): Promise<void> {
  const query = description.replace(/\s+/g, ' ').trim();
  draftStore.set((d) => ({ describe: { query }, focus: d.focus + 1 }));
  navigate({ view: 'finder', record: null, admin: null, params: { mode: 'describe', s: null } });
  if (query.length < 8) return;
  let meta;
  try {
    meta = await queryClient.fetchQuery(metaQuery);
  } catch {
    // The form is filled in and the view shows what went wrong.
    return;
  }
  if (!meta.connected || !meta.defaults.finder) return;
  const ok = await confirmDialog({
    title: 'Run this AI search?',
    body: `AI searches the web for companies that match “${query}”.`,
    confirmLabel: 'Run search',
    icon: 'search',
  });
  if (!ok) return;
  try {
    const s = await startSearch({ mode: 'describe', query, count: DEFAULT_COUNT });
    rememberRecent({ kind: 'finder', id: s.id, title: s.title, subtitle: MODE_LABELS[s.mode] });
    navigate({ view: 'finder', record: null, admin: null, params: { s: s.id, mode: null } });
  } catch (err) {
    toastError(err);
  }
}

/** Confirms the cost first, then starts one look-up for the companies and offers its progress in the AI Finder. */
async function findKeyPeople(companyIds: string[]): Promise<void> {
  const n = companyIds.length;
  let started: (FinderSearchSummary & { companies: number; skipped: number }) | null = null;
  const ok = await confirmDialog({
    title: n === 1 ? 'Find key people at this company?' : `Find key people at ${n} companies?`,
    body:
      n === 1
        ? 'AI searches the web for its decision makers, as one paid AI look-up. The people it finds wait on the company for you to add or dismiss.'
        : `AI searches the web for the decision makers at each company, as ${n} paid AI look-ups. The people it finds wait on each company for you to add or dismiss.`,
    confirmLabel: 'Find key people',
    icon: 'sparkle',
    action: async () => {
      started = await api.post<FinderSearchSummary & { companies: number; skipped: number }>('/finder/people/companies', { companyIds });
    },
  });
  const run = started as (FinderSearchSummary & { companies: number; skipped: number }) | null;
  if (!ok || !run) return;
  void queryClient.invalidateQueries({ queryKey: ['finder'] });
  const busy = run.skipped ? ` · ${plural(run.skipped, 'company is', 'companies are')} already being looked up` : '';
  toast(`Finding key people at ${plural(run.companies, 'company', 'companies')}${busy}`, {
    icon: 'sparkle',
    action: { label: 'Show progress', run: () => openFinder(run.id) },
  });
}

function openFinder(searchId: string): void {
  navigate({ view: 'finder', record: null, admin: null, params: { s: searchId, mode: null } });
}

export const area: ClientArea = {
  name: 'finder',
  setup() {
    registerView('finder', () => import('./FinderView'));
    registerActions({
      finderAsk: (description) => void finderAsk(description),
      openFinder,
      findKeyPeople: (companyIds) => void findKeyPeople(companyIds),
    });
    registerGlobal('finder.live', FinderLive);
    registerSlot('record.fill', RecordFillSlot);
    registerSlot('record.people', RecordPeopleSlot);
    registerCommands(() => [
      {
        id: 'finder-new',
        title: 'New AI search',
        sub: 'Describe the companies to find',
        icon: 'sparkle',
        keywords: 'ai finder find companies people leads prospects search web',
        order: 130,
        run: () => openForm('describe'),
      },
    ]);
  },
};
